import { IconButton, Theme } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import React, { forwardRef, PropsWithChildren, RefAttributes } from "react";

export interface Props {
    icon: React.ElementType | React.ReactNode;
    iconRight?: React.ElementType;
    active?: boolean;
    label?: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string;
    testId?: string;
    iconClassName?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
    button: {
        border: "1px solid rgba(0, 0, 0, 0.54)",
        borderRadius: "4px",
        transition: theme.transitions.create(["background-color", "border-color"]),
        margin: "0.25rem",
        color: "rgba(0, 0, 0, 0.87)",
        whiteSpace: "nowrap",
        padding: "5px",
        flexShrink: 0,
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            borderColor: "rgba(0, 0, 0, 0.87)",
            color: "rgba(0, 0, 0, 0.87)"
        }
    },
    buttonActive: {
        borderColor: "rgba(0, 0, 0, 0.87)",
        color: "white",
        backgroundColor: "rgba(0, 0, 0, 0.87)",
        pointerEvents: "none"
    },
    tooltip: {
        backgroundColor: "rgba(0,0,0,0.87)"
    },
    arrow: {
        color: "rgba(0,0,0,0.87)"
    }
}));

type MenuButtonType = React.FC<PropsWithChildren<Props> & RefAttributes<HTMLButtonElement>>;
const MenuIconButton: MenuButtonType = forwardRef<HTMLButtonElement, Props>((props, ref) => {
    const classes = useStyles();

    const icon = React.isValidElement(props.icon)
        ? props.icon
        : React.createElement((props.icon as React.ElementType), {
            className: props.iconClassName
        });

    const button = (
        <IconButton
            data-cy={props.testId}
            color="primary"
            ref={ref}
            size="small"
            onClick={props.onClick}
            className={clsx(
                classes.button,
                props.active && classes.buttonActive,
                props.className
            )}>

            {icon}

            {props.iconRight && React.createElement(props.iconRight)}

        </IconButton>
    );

    if (props.label) {
        return (
            <Tooltip
                arrow
                placement="top"
                title={props.label}
                classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}>

                {button}

            </Tooltip>
        );
    }

    return button;
});

export default MenuIconButton;
